import SaveMessageDialog from '~/components/shared/SaveMessageDialog';
import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {View} from 'react-native';
import {Divider} from 'react-native-paper';
import moment from 'moment';
import sy from '~/styles';
import {
  Text,
  Pressable,
  PrimaryButton,
  ServiceRequestStatusIcon,
  InputChatFloating,
} from '~/components/controls';
import {service_request as serviceRequestApi} from '~/api/private';
import {
  canInvoice,
  isArchived,
  isNotReadyForInvoicing,
  isRobRequestReady,
} from '~/types/rob';
import {STATUS_CODES} from '~/types/statuses';
import {REPLACEMENT_TYPE} from '~/types/replacement';
import {showDoneClientMessageDialog} from '~/actions';
import {useDispatch} from '~/lib/hooks';
import {Tabs} from '~/types/tabs';
import Format from '~/lib/format';
import ErrorIcon from '~/images/md-icons/error/materialicons/24px.svg';
const hasOpenTasks = (overview, is_fleet_car) => {
  let open_tasks =
    !is_fleet_car &&
    overview.components
      .flatMap((c) => c.tasks ?? [])
      ?.filter((task) => task.is_main_task)
      .some(
        (task) =>
          !(overview.dealer_request ?? overview.rob)?.components[task.id],
      );
  if (
    !open_tasks &&
    overview.replacement?.type === REPLACEMENT_TYPE.transport &&
    overview.replacement?.sla?.rob_code &&
    overview.rob
  ) {
    open_tasks = Object.values(overview.rob.components).every(
      (item) => item.rob.code !== overview.replacement.sla.rob_code,
    );
  }
  return open_tasks;
};
const getRequestReadyState = ({
  robContract,
  overview,
  is_fleet_car,
  is_retail_car,
  open_tasks,
  amount,
}) => {
  let ready = isRobRequestReady(
    robContract,
    overview.components,
    overview.dealer_request ?? overview.fleet_request ?? overview.rob,
    is_fleet_car,
    is_retail_car,
  );
  if (
    overview.components?.length === 0 &&
    overview.replacement?.type === REPLACEMENT_TYPE.transport &&
    overview.replacement?.sla?.rob_code &&
    amount > 0
  ) {
    ready = true;
  }
  if (
    (is_retail_car || is_fleet_car) &&
    overview.components?.length === 0 &&
    overview.status === STATUS_CODES.Modified &&
    !ready
  ) {
    ready = true;
  }
  return ready;
};
const renderReadyDoneButton = ({
  request_id,
  is_fleet_car,
  overview,
  ready,
  changeStatus,
  dispatch,
}) => {
  const phone_number_missing =
    overview?.replacement?.type === REPLACEMENT_TYPE.message &&
    !overview?.phone_number;
  return (
    <PrimaryButton
      onPress={() => {
        if (!is_fleet_car && (overview?.phone_number || overview?.email)) {
          dispatch(showDoneClientMessageDialog(request_id));
        }
        changeStatus(STATUS_CODES.Done);
      }}
      disabled={
        isNotReadyForInvoicing(
          (overview?.fleet_request ?? overview?.rob)?.invoicing?.status,
        ) ||
        moment(overview?.datetime).isAfter(moment(), 'day') ||
        phone_number_missing ||
        !ready
      }
      icon={() => (
        <ServiceRequestStatusIcon
          status={STATUS_CODES.Done}
          fill="#FFFFFF"
          iconProps={{
            width: 18,
            height: 18,
          }}
        />
      )}>
      GEREED MELDEN
    </PrimaryButton>
  );
};
const renderReservedButton = (changeStatus) => (
  <PrimaryButton
    onPress={() => changeStatus(STATUS_CODES.CheckIn)}
    icon={() => (
      <ServiceRequestStatusIcon
        status={STATUS_CODES.CheckIn}
        fill="#FFFFFF"
        iconProps={{
          width: 18,
          height: 18,
        }}
      />
    )}>
    INCHECKEN
  </PrimaryButton>
);
const renderRetailApproveButton = ({ready, changeStatus}) => (
  <PrimaryButton
    style={
      ready && {
        backgroundColor: '#299976',
      }
    }
    onPress={() => changeStatus(STATUS_CODES.Approved)}
    disabled={!ready}
    icon={() => (
      <ServiceRequestStatusIcon
        status={STATUS_CODES.Approved}
        fill="#FFFFFF"
        iconProps={{
          width: 18,
          height: 18,
        }}
      />
    )}>
    GOEDKEUREN
  </PrimaryButton>
);
const renderApprovalRequestedButton = () => (
  <PrimaryButton
    disabled={true}
    icon={() => (
      <ServiceRequestStatusIcon
        status={STATUS_CODES.ApprovalRequested}
        fill="rgba(0, 0, 0, 0.32)"
        iconProps={{
          width: 18,
          height: 18,
        }}
      />
    )}>
    VOORSTELLEN
  </PrimaryButton>
);
const renderProposalButton = ({
  ready,
  pendingMessage,
  setShowSaveMessageDialog,
  onDoRequest,
}) => (
  <PrimaryButton
    disabled={!ready}
    onPress={async () => {
      if (pendingMessage?.length > 0) {
        setShowSaveMessageDialog(true);
        return true;
      }
      await onDoRequest();
    }}
    icon={() => (
      <ServiceRequestStatusIcon
        status={STATUS_CODES.ApprovalRequested}
        fill="#FFFFFF"
        iconProps={{
          width: 18,
          height: 18,
        }}
      />
    )}>
    VOORSTELLEN
  </PrimaryButton>
);
const renderInvoiceButton = ({overview, invoice, onInvoice}) => (
  <PrimaryButton
    disabled={
      (!overview?.invoice_number && !invoice?.bahnkick_can_invoice) ||
      (!overview.fleet_request &&
        (overview.dealer_request ?? overview.rob)?.invoicing?.status !==
          'ReadyForInvoicing')
    }
    onPress={onInvoice}
    icon={() => (
      <ServiceRequestStatusIcon
        status={STATUS_CODES.Invoiced}
        fill="#FFFFFF"
        iconProps={{
          width: 18,
          height: 18,
        }}
      />
    )}>
    FACTUREREN
  </PrimaryButton>
);
const buildSubmitButton = ({
  request_id,
  is_fleet_car,
  is_retail_car,
  status,
  overview,
  robContract,
  invoice,
  amount,
  pendingMessage,
  dispatch,
  setShowSaveMessageDialog,
  changeStatus,
  onDoRequest,
  onInvoice,
}) => {
  const open_tasks = hasOpenTasks(overview, is_fleet_car);
  if (
    isArchived(overview?.rob?.status) ||
    overview?.status === STATUS_CODES.Cancelled
  ) {
    return <></>;
  }
  if (canInvoice(status) && !open_tasks) {
    const ready = isRobRequestReady(
      robContract,
      overview.components,
      overview.dealer_request ?? overview.fleet_request ?? overview.rob,
      is_fleet_car,
      is_retail_car,
    );
    return renderReadyDoneButton({
      request_id,
      is_fleet_car,
      overview,
      ready,
      changeStatus,
      dispatch,
    });
  }
  if (status === STATUS_CODES.Reserved) {
    return renderReservedButton(changeStatus);
  }
  if (
    is_retail_car &&
    [STATUS_CODES.CheckIn, STATUS_CODES.Modified].includes(status)
  ) {
    const ready = isRobRequestReady(
      robContract,
      overview.components,
      null,
      is_fleet_car,
      is_retail_car,
    );
    return renderRetailApproveButton({
      ready,
      changeStatus,
    });
  }
  if (status === STATUS_CODES.ApprovalRequested) {
    return renderApprovalRequestedButton();
  }
  if (
    status === STATUS_CODES.CheckIn ||
    status === STATUS_CODES.Modified ||
    status === STATUS_CODES.Denied ||
    open_tasks
  ) {
    const ready = getRequestReadyState({
      robContract,
      overview,
      is_fleet_car,
      is_retail_car,
      open_tasks,
      amount,
    });
    return renderProposalButton({
      ready,
      pendingMessage,
      setShowSaveMessageDialog,
      onDoRequest,
    });
  }
  if ([STATUS_CODES.Done, STATUS_CODES.DoneNoInvoice].includes(status)) {
    return renderInvoiceButton({
      overview,
      invoice,
      onInvoice,
    });
  }
  return <></>;
};
const Footer = ({
  request_id,
  is_fleet_car,
  is_retail_car,
  editable,
  status,
  overview,
  robContract,
  invoice,
  amount,
  robErrors,
  onDoRequest,
  onRefresh,
  onInvoice,
  onShowRobRequestErrors,
  onChangeTab,
}) => {
  const dispatch = useDispatch();
  const [pendingMessage, setPendingMessage] = useState(null);
  const [showSaveMessageDialog, setShowSaveMessageDialog] = useState(false);
  const onSendChatMessage = async () => {
    const {success} = await serviceRequestApi.send_message(
      request_id,
      pendingMessage,
    );
    if (success) {
      setPendingMessage(null);
      onRefresh();
    }
  };
  const onSendChatAttachment = async (attachment) => {
    const {success} = await serviceRequestApi.send_attachment(
      request_id,
      attachment,
    );
    if (success) {
      onRefresh();
    }
  };
  const changeStatus = async (change) => {
    const {success} = await serviceRequestApi.change_status(request_id, change);
    if (success) {
      onRefresh();
    }
  };
  const submitButton = buildSubmitButton({
    request_id,
    is_fleet_car,
    is_retail_car,
    status,
    overview,
    robContract,
    invoice,
    amount,
    pendingMessage,
    dispatch,
    setShowSaveMessageDialog,
    changeStatus,
    onDoRequest,
    onInvoice,
  });
  const rob_request_errors = robErrors?.filter(
    (item) => item.task_id === null || item.task_id === undefined,
  );
  return (
    status !== STATUS_CODES.Invoiced && (
      <>
        {editable && (
          <>
            {is_retail_car ? null : (
              <InputChatFloating
                is_rob={!is_fleet_car && !is_retail_car}
                message={pendingMessage}
                onChange={setPendingMessage}
                onSend={onSendChatMessage}
                onAttachment={onSendChatAttachment}
                onViewSentMessage={() => onChangeTab(Tabs.Messages)}
              />
            )}

            <SaveMessageDialog
              showSaveMessageDialog={showSaveMessageDialog}
              setShowSaveMessageDialog={setShowSaveMessageDialog}
              onSendChatMessage={onSendChatMessage}
            />
          </>
        )}

        <Divider style={sy['bg-gray88']} />
        <View
          style={[
            sy['bg-white'],
            sy['flex-row'],
            sy['justify-between'],
            sy['items-center'],
            sy['py-3'],
            sy['px-4'],
          ]}>
          {canInvoice(status) ? (
            <View></View>
          ) : (
            <Text style={sy.mediumPlus}>{Format.price(amount)}</Text>
          )}
          <View style={[sy['flex-row'], sy['items-center'], sy['gap-4']]}>
            {rob_request_errors?.length > 0 && (
              <Pressable onPress={onShowRobRequestErrors}>
                <ErrorIcon fill="#972727" />
              </Pressable>
            )}
            {submitButton}
          </View>
        </View>
      </>
    )
  );
};
Footer.propTypes = {
  request_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  is_fleet_car: PropTypes.bool,
  is_retail_car: PropTypes.bool,
  editable: PropTypes.bool,
  status: PropTypes.string,
  overview: PropTypes.object,
  robContract: PropTypes.object,
  invoice: PropTypes.object,
  amount: PropTypes.number,
  robErrors: PropTypes.array,
  onDoRequest: PropTypes.func,
  onRefresh: PropTypes.func,
  onInvoice: PropTypes.func,
  onShowRobRequestErrors: PropTypes.func,
  onChangeTab: PropTypes.func,
};
export default Footer;
